import React, { useEffect, useRef, useState } from 'react';
import { X, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';

const GOLD = '#C7A46D';
const GOLD_GRAD = 'linear-gradient(135deg, #C7A46D, #B5925F)';

/**
 * OTPModal: 6-digit code login flow.
 * Stays in the same browser tab (no magic-link bounce). Mobile-optimised.
 *
 * Props:
 *   open: boolean
 *   email: string, pre-filled from quiz
 *   onClose: () => void
 *   onSuccess: (user) => void, called after successful verification
 */
export default function OTPModal({ open, email, accessCode = '', onClose, onSuccess }) {
  const { setUser } = useAuth();
  const [phase, setPhase] = useState('requesting');   // requesting | entry | verifying | success
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifiedUser, setVerifiedUser] = useState(null);
  const inputsRef = useRef([]);

  // Request OTP on open
  useEffect(() => {
    if (!open || !email) return;
    setPhase('requesting');
    setError(null);
    setDigits(['', '', '', '', '', '']);
    setVerifiedUser(null);
    api.post('/auth/otp/request', { email })
      .then(() => {
        setPhase('entry');
        setResendCooldown(30);
        setTimeout(() => inputsRef.current[0]?.focus(), 60);
      })
      .catch(err => {
        setPhase('entry');
        setError(err?.response?.data?.detail || 'Could not send code. Please try again.');
      });
  }, [open, email]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  if (!open) return null;

  const code = digits.join('');
  const localQaEnabled = process.env.NODE_ENV === 'development'
    && ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const continueLocalQa = () => {
    const user = { email, id: null, local: true, qa: true };
    setUser(user);
    setPhase('success');
    setTimeout(() => onSuccess?.(user), 400);
  };

  const handleChange = (i, val) => {
    const v = val.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setError(null);
    if (v && i < 5) inputsRef.current[i + 1]?.focus();
    if (next.join('').length === 6) submit(next.join(''));
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = pasted.split('').concat(['', '', '', '', '', '']).slice(0, 6);
    setDigits(next);
    setError(null);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6) submit(pasted);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
  };

  const submit = async (codeOverride) => {
    const c = codeOverride || code;
    if (c.length !== 6) return;
    setPhase('verifying');
    setError(null);
    try {
      const r = await api.post('/auth/otp/verify', { email, code: c, access_code: accessCode || null });
      setUser(r.data.user, r.data.session_token);
      setVerifiedUser(r.data.user);
      setPhase('success');
      setTimeout(() => onSuccess?.(r.data.user), 900);
    } catch (err) {
      setPhase('entry');
      setError(err?.response?.data?.detail || 'Invalid code. Try again.');
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    }
  };

  const resend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setError(null);
    try {
      await api.post('/auth/otp/request', { email });
      setResendCooldown(30);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not resend. Try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(3,5,10,0.85)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 440,
          background: '#0c0c0c', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 20, padding: '36px 28px',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 14, right: 14,
            background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)',
            cursor: 'pointer', padding: 6,
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {phase === 'success' ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <CheckCircle2 size={56} style={{ color: '#1D9E75' }} />
            </div>
            <h2 className="font-head" style={{ fontSize: 22, color: '#fff', fontWeight: 700, marginBottom: 8 }}>
              {verifiedUser?.life_os_access ? "You're in." : 'Email verified.'}
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
              {verifiedUser?.life_os_access ? 'Welcome to MyArc Life OS.' : 'Life OS access is still private.'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div className="font-data" style={{ fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, marginBottom: 12 }}>
                Verify your email
              </div>
              <h2 className="font-head" style={{ fontSize: 22, color: '#fff', fontWeight: 700, marginBottom: 8 }}>
                Enter MyArc Life OS
              </h2>
              <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
                6-digit code sent to <span style={{ color: '#fff' }}>{email}</span>
              </p>
            </div>

            {phase === 'requesting' && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '20px 0', color: 'rgba(255,255,255,0.55)' }}>
                <Loader2 size={16} className="animate-spin" /> Sending code…
              </div>
            )}

            {(phase === 'entry' || phase === 'verifying') && (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 18 }}>
                  {digits.map((d, i) => (
                    <input
                      key={i}
                      ref={el => (inputsRef.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      maxLength={1}
                      value={d}
                      onChange={e => handleChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      onPaste={i === 0 ? handlePaste : undefined}
                      disabled={phase === 'verifying'}
                      style={{
                        width: 44, height: 56,
                        textAlign: 'center', fontSize: 22, fontWeight: 700,
                        color: '#fff', background: 'rgba(255,255,255,0.04)',
                        border: `1px solid ${error ? '#e05252' : d ? GOLD : 'rgba(255,255,255,0.12)'}`,
                        borderRadius: 10, outline: 'none',
                        transition: 'border-color 150ms',
                      }}
                    />
                  ))}
                </div>

                {error && (
                  <p style={{ fontSize: 12.5, color: '#e05252', textAlign: 'center', marginBottom: 14 }}>{error}</p>
                )}

                {phase === 'verifying' ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                    <Loader2 size={14} className="animate-spin" /> Verifying…
                  </div>
                ) : (
                  <button
                    onClick={() => submit()}
                    disabled={code.length !== 6}
                    style={{
                      width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '14px 24px',
                      background: GOLD_GRAD, color: '#0B0A08',
                      fontSize: 12, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
                      border: 'none',
                      clipPath: 'polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)',
                      cursor: code.length === 6 ? 'pointer' : 'not-allowed',
                      opacity: code.length === 6 ? 1 : 0.5,
                      transition: 'opacity 150ms, filter 150ms',
                    }}
                  >
                    Verify <ArrowRight size={14} />
                  </button>
                )}

                <div style={{ marginTop: 22, textAlign: 'center', fontSize: 12.5, color: 'rgba(255,255,255,0.5)' }}>
                  Didn't get it?{' '}
                  <button
                    onClick={resend}
                    disabled={resendCooldown > 0 || resending}
                    style={{
                      background: 'none', border: 'none',
                      color: resendCooldown > 0 ? 'rgba(255,255,255,0.3)' : GOLD,
                      cursor: resendCooldown > 0 ? 'default' : 'pointer',
                      padding: 0, font: 'inherit', textDecoration: resendCooldown > 0 ? 'none' : 'underline',
                    }}
                  >
                    {resending ? 'Resending…' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>

                {localQaEnabled && (
                  <button
                    onClick={continueLocalQa}
                    type="button"
                    style={{
                      marginTop: 16,
                      width: '100%',
                      padding: '12px 18px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 10,
                      color: 'rgba(255,255,255,0.72)',
                      cursor: 'pointer',
                      fontSize: 12,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Local QA: continue without code
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
